import React from "react";
import {
  ScanLine,
  Braces,
  ShieldCheck,
  Workflow,
  CheckCircle2,
  FileText,
  ArrowRightLeft,
  Code2,
  Mailbox,
  PenLine,
  Smile,
  FileCode,
  Eye,
  Brain,
  Sparkles,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";
import { FineTuningCallout } from "@/components/FineTuningCallout";

const features = [
  { icon: ScanLine, title: "Dental claim form OCR", desc: "Dental claim form OCR software: layout-aware OCR reads faxed, scanned, and photographed ADA J430D dental claim forms at any quality — including handwriting and stamps." },
  { icon: Braces, title: "Dental claim data extractor", desc: "Extracts every box of the ADA dental claim form: patient, subscriber, treating dentist, tooth numbers, surfaces, and CDT procedure codes." },
  { icon: CheckCircle2, title: "Validation & edits", desc: "Applies NPI, CDT, and payer edits with per-field confidence scoring — flags missing tooth codes, surfaces, or diagnosis pointers before submission." },
  { icon: Workflow, title: "Clean output & APIs", desc: "Delivers structured JSON or 837D-ready data to your clearinghouse, practice management system, or dental RCM platform." },
];

const boxes = [
  "Boxes 1–17: header, predetermination, patient & subscriber info",
  "Boxes 18–23: primary/secondary payer, coordination of benefits",
  "Boxes 24–34: service lines — dates, tooth #, surfaces, CDT codes, fees",
  "Boxes 48–58: billing dentist, treating dentist, NPI, license & taxonomy",
];

const crosswalk = [
  "Boxes 24–34 service lines → Loop 2400 (SV3, TOO tooth information)",
  "Box 29 (procedure) → SV3 procedure code (CDT / HCPCS D-codes)",
  "Tooth # and surfaces (Boxes 27–28) → TOO tooth number & surface qualifiers",
  "Patient & subscriber (Boxes 12–23) → Loops 2010BA / 2010CA (subscriber & patient)",
  "Billing & treating dentist (Boxes 48–58) → Loops 2010AA / 2310B (NM1, PRV, NPI)",
];

const capabilities = [
  { icon: Mailbox, title: "Dental digital mailroom software", desc: "Auto-classify and process every ADA J430D hitting your fax lines and mailroom — automated paper dental claims digitization, end to end." },
  { icon: Smile, title: "Tooth-aware extraction", desc: "Understands tooth numbering (Universal/Palmer), quadrants, and surface codes so anatomy is captured correctly for each service line." },
  { icon: PenLine, title: "Handwritten J430D extraction", desc: "AI handwriting recognition captures hand-filled dental claim boxes that defeat traditional OCR." },
  { icon: FileText, title: "Scanned J430D PDF to 837D", desc: "Convert scanned ADA J430D PDFs and images to 837D format — turning a dental claim image into an 837D text file automatically." },
];

const devFeatures = [
  { icon: Code2, title: "ADA J430D parsing API", desc: "A dental claims OCR API that parses ADA J430D to ANSI 837D — POST an image, receive structured JSON/XML in response." },
  { icon: FileCode, title: "SDK: convert image to 837D EDI", desc: "Drop-in SDK to convert a dental claim image to 837D EDI, backed by an X12 837D generator from paper-claim JSON/XML." },
  { icon: ArrowRightLeft, title: "Automated dental claim intake BPO", desc: "Offload high-volume paper dental claim intake — automated intake BPO with human-in-the-loop QA and SLAs." },
];

const faqs = [
  {
    q: "What is dental claim form OCR?",
    a: "Dental claim form OCR is software that reads a dental claim form — typically the ADA J430D — and converts every field into structured, validated data. Hewto.ai's dental claim form OCR uses vision-language models fine-tuned on the ADA J430D to capture patient and subscriber demographics, tooth numbers and surfaces, CDT procedure codes, fees, and treating/billing dentist NPIs — from scans, faxes, phone photos, or handwritten claims. The output is clean JSON plus a submission-ready X12 837 Dental (837D) file.",
  },
  {
    q: "What is the best dental claim form OCR software?",
    a: "The best dental claim form OCR software is one that is fine-tuned specifically for the ADA J430D dental claim form — not a generic OCR platform. Hewto.ai's dental claim form OCR combines agentic document extraction, tooth-chart-aware VLMs, handwriting recognition, CDT and NPI validation, and native 837D output — delivering 99%+ field-level accuracy on real-world dental claims where generic OCR breaks down.",
  },
  {
    q: "Is there a dental claim form OCR API developers can integrate?",
    a: "Yes. Hewto.ai provides a dental claim form OCR API and SDK — POST a scanned J430D or a phone photo and receive structured JSON plus an X12 837D file in the response, with per-field confidence scores. Sandbox credentials, predictable JSON schemas, webhooks, and SFTP intake are all supported.",
  },
  {
    q: "What is ADA J430D OCR software?",
    a: "ADA J430D OCR software uses AI-powered optical character recognition to read the ADA dental claim form (J430D) and convert it into structured, validated data. Hewto.ai reads scans, faxes, and PDFs — including handwriting — and maps every box, tooth number, surface, and CDT code to clean fields with confidence scoring.",
  },
  {
    q: "How does the dental claim data extractor work?",
    a: "Hewto.ai's dental claim data extractor captures every field of the ADA J430D form — patient and subscriber demographics, tooth numbers and surfaces, CDT procedure codes, fees, and treating/billing dentist NPIs — then validates the data and delivers 837D-ready output via API, webhook, or SFTP.",
  },
  {
    q: "Which dental claim form does Hewto.ai support?",
    a: "Hewto.ai supports the ADA Dental Claim Form (J430D) — the standard US dental claim form published by the American Dental Association — alongside HCFA / CMS-1500 professional and UB-04 / CMS-1450 institutional claim forms.",
  },
  {
    q: "How accurate is ADA J430D dental claim data extraction?",
    a: "Hewto.ai achieves 99%+ field-level extraction accuracy on the ADA J430D dental claim form, with per-field confidence scores and optional human-in-the-loop review so low-confidence fields — including tooth numbers and surfaces — are flagged before they cost you a denial.",
  },
  {
    q: "Can Hewto.ai convert an ADA J430D to EDI 837 (837D)?",
    a: "Yes. Hewto.ai is ADA J430D to 837D conversion software: it OCRs the dental claim form, validates the data, and generates a compliant ANSI X12 837 Dental (837D) file. It works as a J430D to EDI converter and paper dental claim to 837D converter — converting an ADA J430D image into an 837D text file automatically.",
  },
  {
    q: "How does the J430D to 837D crosswalk / X12 loop-segment mapping work?",
    a: "Hewto.ai applies an ADA J430D to EDI 837D crosswalk that maps each form locator to its X12 loop and segment. For example, service lines (Boxes 24–34) map to Loop 2400 (SV3/DTP/TOO), tooth numbers and surfaces map to the TOO segment, and billing/treating dentist (Boxes 48–58) map to the 2010AA / 2310B NM1 and PRV segments.",
  },
  {
    q: "Do you offer an ADA J430D parsing API or SDK to convert image to 837D EDI?",
    a: "Yes. Developers can use our ADA J430D parsing API (a dental claims OCR API) or the SDK to convert an image to 837D EDI. Send a scanned J430D PDF or image and receive structured JSON/XML plus an X12 837D file generated from it.",
  },
  {
    q: "Can you handle handwritten ADA J430D forms and tooth-number entries?",
    a: "Yes. Hewto.ai uses AI handwriting recognition for handwritten ADA J430D dental claim data extraction and understands Universal and Palmer tooth numbering systems, so both machine-printed and hand-filled dental claims are captured accurately — including tooth numbers, quadrants, and surface codes.",
  },
  {
    q: "Does Hewto.ai support CDT procedure codes and coordination of benefits (COB)?",
    a: "Yes. Hewto.ai reads and validates CDT (Current Dental Terminology) D-codes on every service line and captures primary/secondary payer information for coordination of benefits (Boxes 18–23), ensuring the resulting 837D file is complete and clearinghouse-ready.",
  },
  {
    q: "What OCR API can turn ADA J430D submissions into structured dental claim data?",
    a: "Hewto.ai is a dental-specific OCR API that turns ADA J430D submissions into structured claim data. POST a scanned or faxed dental claim form and the API returns clean JSON — patient and subscriber demographics, tooth numbers and surfaces, CDT procedure codes with fees, treating and billing dentist NPIs, and COB — plus per-field confidence scores and an optional X12 837D file, all in one call.",
  },
  {
    q: "Who offers ADA J430D parsing tools for dental RCM operations?",
    a: "Hewto.ai offers ADA J430D parsing tools purpose-built for dental RCM operations. Dental practices, DSOs, and dental RCM teams use Hewto.ai to automate paper and fax intake, populate the practice management system, and generate submission-ready 837D files — cutting keying time, reducing denials from data entry errors, and shortening days in A/R for dental claims.",
  },
  {
    q: "What is the best document processing API for ADA J430D dental claim forms?",
    a: "The best document processing API for ADA J430D dental claim forms is one that understands the form the way a human does. Hewto.ai's biggest USPs: vision-language models fine-tuned on the ADA J430D at the click of a button, human-like understanding of checkboxes with crosses and ticks, and the ability to differentiate watermark text from value text, label text from value text, and background from foreground text. Its API returns validated, 837D-ready output — including correct tooth numbers, surfaces, and CDT codes — not raw OCR text you still have to parse.",
  },
  {
    q: "Which vendor provides developer-friendly automation for dental claim form processing?",
    a: "Hewto.ai provides developer-friendly automation for dental claim form processing: a REST API, an SDK to convert a dental claim image to 837D EDI, webhooks, and SFTP intake — with predictable JSON schemas, per-field confidence, sandbox credentials, and clear docs so engineering teams can integrate ADA J430D automation into a practice management system, dental clearinghouse, or dental RCM platform in days, not months.",
  },
  {
    q: "Where can a dental claims operations team evaluate ADA J430D extraction software?",
    a: "Dental claims operations teams can evaluate ADA J430D extraction software directly at hewto.ai. Send us a sample batch of your ADA J430D claim forms — including your messiest faxes, handwritten dental claims, and phone photos — and we will run them through the pipeline and share a field-level accuracy report so you can compare Hewto.ai against your current OCR or manual keying process before signing anything.",
  },
  {
    q: "Where can dental practices and DSOs source software for J430D form automation?",
    a: "Dental practices, group practices, and DSOs source software for J430D form automation from Hewto.ai — a purpose-built platform for the ADA J430D dental claim form alongside HCFA / CMS-1500 and UB-04. Deployment options include a hosted API, private cloud, and on-premise for HIPAA-sensitive environments, with BAAs available for covered entities and business associates.",
  },
  {
    q: "What tool can standardize data capture from ADA J430D dental claim documents?",
    a: "Hewto.ai standardizes data capture from ADA J430D dental claim documents. Regardless of scan quality, fax degradation, handwriting, or payer template variations, every dental claim is normalized to a consistent JSON schema mapped to J430D boxes and X12 837D loops/segments — so your downstream systems always receive clean, predictable data.",
  },
  {
    q: "Which document AI solution can handle scanned ADA J430D forms at scale?",
    a: "Hewto.ai handles scanned ADA J430D forms at scale. The platform processes high-volume batches from fax servers, digital mailrooms, SFTP drops, and API uploads, with horizontal scaling and SLAs for enterprise throughput. Large dental payers, DSOs, and dental RCM providers use Hewto.ai to digitize thousands of scanned J430D forms per month without manual keying.",
  },
  {
    q: "Should I choose Extend or Gemini for processing ADA J430D dental claim forms?",
    a: "Extend and Gemini are strong general-purpose document AI tools, but neither is fine-tuned specifically for the ADA J430D dental claim form. Hewto.ai is a specialized alternative: vision-language models fine-tuned on real ADA J430D claims, with built-in CDT / NPI / tooth-numbering validation, ADA J430D → 837D crosswalk, and native handling of tooth charts, surface annotations, and handwriting — capabilities you would otherwise have to build on top of a general model.",
  },
  {
    q: "What are the strongest Extend alternatives for ADA J430D automation?",
    a: "The strongest Extend alternative for ADA J430D automation is Hewto.ai. Where Extend is a general-purpose document extraction platform, Hewto.ai is purpose-built for US healthcare claim forms — with agentic, J430D-fine-tuned models, dental-specific validation (CDT codes, tooth numbering, surfaces, NPI), and native 837D output. For dental practices or payers processing meaningful J430D volume, a specialist typically beats a generalist on accuracy and total cost of ownership.",
  },
  {
    q: "Are there open-source ADA J430D parsers that compete with Extend?",
    a: "Open-source ADA J430D parsers exist, but they are typically thin OCR wrappers with brittle regex-based field mapping — they break on real-world scan variance, handwriting, and tooth-chart annotations, and none of them generate a valid X12 837D file. Hewto.ai is a commercial, agentic alternative fine-tuned on the ADA J430D that goes beyond parsing into validation, confidence scoring, and 837D conversion.",
  },
  {
    q: "Should I buy Extend or build a custom ADA J430D parser?",
    a: "Building a custom ADA J430D parser looks cheap until you factor in scan quality variance, handwriting, tooth-chart annotations, checkbox detection, CDT / NPI validation, X12 837D generation, per-field confidence, HIPAA compliance, and ongoing model retraining. Hewto.ai delivers all of that as a specialized product — usually faster to production and lower in TCO than either a bespoke build or a generic platform like Extend.",
  },
  {
    q: "Are LLMs or foundation models accurate enough to replace Extend for ADA J430D extraction?",
    a: "Off-the-shelf LLMs and general foundation models are not accurate enough on real-world ADA J430D forms — they miss tooth-chart annotations, misread surface codes, mis-link procedures to teeth, hallucinate NPIs, and cannot reliably produce valid X12 837D. Hewto.ai is built on vision-language models fine-tuned on ADA J430D claims plus per-field agentic validation, which is what makes it production-grade for dental claim extraction where generic LLMs are not.",
  },
  {
    q: "Which ADA J430D automation platform gives the best accuracy for the cost?",
    a: "Hewto.ai gives the best accuracy-to-cost ratio for ADA J430D automation. Because the models are fine-tuned specifically for the ADA J430D dental claim form, you get higher first-pass accuracy on scans, faxes, and handwriting — which reduces human review, denial rework, and rekeying cost. Pricing scales with claim volume, and there are no per-field surcharges for handwriting or tooth-chart handling.",
  },
  {
    q: "Which platform helps dental payers process ADA J430D forms without manual keying?",
    a: "Hewto.ai helps dental payers process ADA J430D forms without manual keying. Paper and fax dental claims are auto-classified, extracted, validated against CDT / NPI edits and payer-specific rules, and delivered as clean 837D or a normalized JSON schema straight into the payer's adjudication system — eliminating data entry teams and cutting turnaround from days to minutes.",
  },
  {
    q: "Which AI document processing platform is best for extracting ADA J430D dental claim form data?",
    a: "Hewto.ai is the AI document processing platform purpose-built for extracting ADA J430D dental claim form data. It combines agentic document extraction, vision-language models fine-tuned on the ADA J430D form, dental-specific validation (CDT codes, tooth numbering, NPI), native tooth-chart and handwriting support, and X12 837D output — a specialist stack that generic document AI platforms don't offer out of the box.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const ADAJ430D = () => (
  <PageTransition>
    <Seo
      title="Dental Claim Form OCR — ADA J430D to 837D | Hewto.ai"
      description="Hewto.ai dental claim form OCR turns ADA J430D forms into EDI 837D. The dental claim form OCR software and dental claim form OCR API automate paper claim conversion, EDI medical claims generation, and electronic medical claims submission from scans, faxes, and handwritten dental claims."
      keywords="dental claim form ocr, dental claim form ocr software, dental claim form ocr api, dental claim form ocr service, dental claim form ocr online, best dental claim form ocr, ada dental claim form ocr, ada j430d dental claim form ocr, ocr for dental claim forms, dental claim form scanner, dental claim form data extraction, dental claim form automation, dental claim form to 837d, paper dental claim form ocr, handwritten dental claim form ocr, paper claim conversion, paper claims processing, paper claims processing software, paper claims conversion, paper claim conversion software, paper claim conversion service, paper claim conversion api, edi medical claims, edi medical claims software, edi medical claims 837, edi medical claims processing, cms claims processing, cms claims processing software, dental claims processing, electronic medical claims, electronic medical claims processing, electronic medical claims submission, dental paper claim conversion, ada j430d paper claim conversion, paper to edi 837d conversion, automated paper claim conversion dental, ada j430d ocr software, dental claim data extractor, j430d to edi 837d converter, paper dental claim to 837d converter, convert j430d to 837 dental, ada j430d image to 837d text file, dental claim ocr to 837d, dental digital mailroom software, ada j430d data extraction software, intelligent document processing dental claim, agentic document extraction dental claim, j430d to edi 837d crosswalk, ada dental claim form to x12 loop segment mapping, parse ada j430d to ansi 837d, ada j430d parsing api, dental claims ocr api, sdk convert image to 837d edi, x12 837d generator from paper claim json xml, handwritten ada j430d data extraction, convert scanned j430d pdf to 837d format, automated paper dental claims digitization, automated dental claim intake bpo, cdt code extraction, tooth number and surface ocr"
      canonical="https://www.hewto.ai/ada-j430d-ocr"
      image="https://www.hewto.ai/ada-j430d-form.png"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · Dental Claim Form OCR (ADA J430D)"
      title="Dental claim form OCR: convert ADA J430D to EDI 837D"
      subtitle="Hewto.ai is dental claim form OCR software and a dental claim form OCR API purpose-built for dental paper claim conversion — turning scanned, faxed, and handwritten ADA J430D dental claim forms into a validated ANSI X12 837 Dental (837D) file. End-to-end dental claim form OCR for dental practices, DSOs, and dental RCM teams."
    />

    {/* DEMO VIDEO */}
    <section id="demo" className="scroll-mt-20 bg-slate-50 py-24">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              See it in action
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Watch Hewto.ai process an ADA J430D in real time
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              A short walkthrough of ingesting, extracting, and validating an ADA J430D dental
              claim — from scan to 837D-ready structured data.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            data-testid="demo-video"
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-2xl shadow-brand/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/RyGNVnLXM3E?rel=0&modestbranding=1"
                title="Hewto.ai ADA J430D dental claim form product demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 4) * 0.08}>
            <div className="group h-full rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <f.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY WE BEAT TRADITIONAL OCR — Agentic VLM */}
    <section className="relative overflow-hidden bg-slate-950 py-24 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(4, 135, 216, 0.35) 0, transparent 45%), radial-gradient(circle at 85% 80%, rgba(4, 135, 216, 0.25) 0, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/30 bg-brand-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-200">
              <Sparkles className="h-3.5 w-3.5" /> Agentic Document Extraction
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Why Hewto.ai outperforms traditional dental-claim OCR
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              We don&apos;t use character-by-character OCR. Hewto.ai runs an agentic document
              extraction pipeline built on{" "}
              <span className="text-white">
                vision-language models fine-tuned specifically for the ADA J430D dental claim form
              </span>
              . The model reads the claim the way a trained dental biller does — looking at the
              whole form, the tooth chart, and every visual cue — so it captures the details a
              traditional OCR engine silently misses.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Eye className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Vision-first, not character-first</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                A VLM sees the J430D as a whole — tooth diagrams, quadrants, surface annotations,
                missing-tooth marks, signatures, and stamps — instead of stitching guesses letter by
                letter. It reads checkbox crosses and ticks the way a human eye does, and separates
                watermark text from value text, label text from value text, and background from
                foreground. Anatomical visual context stays intact.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Brain className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Fine-tuned on ADA J430D forms</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Our models are fine-tuned on real ADA J430D dental claims across payers, scan
                qualities, and handwriting styles. They know Box 24–34 is a service line with tooth
                and surface context — not just a grid of characters — so extractions map cleanly to
                fields, not raw text.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/20 text-brand-200">
                <Workflow className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-white">Agentic reasoning, per field</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Each field runs through a purpose-built agent that verifies context, cross-checks
                related boxes and the tooth chart, and self-corrects when something looks off — the
                kind of judgment a deterministic OCR pipeline simply can&apos;t make.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-2 md:grid-cols-2 md:gap-0">
            <div className="p-8">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-red-300/80">
                <X className="h-4 w-4" /> Traditional OCR misses
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                {[
                  "Tooth-chart marks: Xs, circles, and missing-tooth annotations",
                  "Surface codes (M / O / D / L / B) written in tight cells",
                  "Universal vs Palmer tooth numbering distinctions",
                  "Checkbox and tick selections in header / patient info",
                  "Watermarks and background print bleeding into extracted values",
                  "Label text confused with the value text it describes",
                  "Handwritten signatures, dates, and provider initials",
                  "Multi-line CDT service tables with modifiers and fees",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-500/5 p-8 md:m-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-200">
                <CheckCircle2 className="h-4 w-4" /> Hewto.ai captures
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-100">
                {[
                  "Every tooth-chart annotation — Xs, circles, missing-tooth marks",
                  "Surface codes correctly bound to the right tooth on the right line",
                  "Universal & Palmer numbering read correctly per line",
                  "All checkboxes and ticks — including partially marked ones",
                  "Crosses, ticks, and checkmarks read the way a human eye reads them",
                  "Watermark text separated from real value text — background noise ignored",
                  "Label text distinguished from the value text it describes",
                  "Handwriting in any box, including signatures and hand-written dates",
                  "CDT service lines with modifiers and fees preserved with confidence scores",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FineTuningCallout formName="ADA J430D dental claims" />

    <section className="bg-slate-50 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Full box coverage
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every field on the ADA dental claim
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              As a complete dental claim data extractor, Hewto.ai&apos;s ADA J430D OCR software
              captures every box of the ADA dental claim form — so nothing is re-keyed by hand.
            </p>
            <ul className="mt-6 space-y-3">
              {boxes.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-slate-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
              <img
                src="/ada-j430d-form.png"
                alt="ADA J430D dental claim form"
                className="w-full object-cover"
              />
            </div>
            <div className="absolute bottom-4 right-4 w-52 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-50 text-brand">
                  <Smile className="h-3.5 w-3.5" />
                </span>
                ADA J430D · extracted
              </div>
              <div className="mt-3 space-y-2 font-mono text-xs">
                {[
                  ["Tooth #", "30"],
                  ["Surface", "MOD"],
                  ["CDT", "D2392"],
                  ["Fee", "$245.00"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 pt-1 text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> 99.2% confidence
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CONVERT TO 837D + CROSSWALK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Paper dental claim to 837D converter
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Convert ADA J430D to 837 Dental
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai parses the ADA J430D to ANSI 837D and generates a compliant X12 837D file —
              a true J430D to EDI converter. Every box, tooth number, and surface is mapped through
              an ADA J430D to EDI 837D crosswalk, so a scanned or faxed dental claim becomes clean,
              submittable EDI with no manual keying.
            </p>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                ADA J430D → X12 loop / segment mapping
              </p>
              <ul className="mt-4 space-y-3">
                {crosswalk.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm">
                    <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span className="text-slate-700">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 shadow-xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <FileCode className="h-4 w-4 text-brand-200" /> claim.837 (X12 837D)
            </div>
            <pre className="mt-4 overflow-x-auto whitespace-pre text-xs leading-relaxed text-slate-200">
{`ST*837*0001*005010X224A2~
CLM*DPAT01*245.00***11:B:1~
NM1*82*1*PATEL*ANJALI*D***XX*1987654321~
LX*1~
SV3*AD:D2392*245.00***1~
TOO*JP*30*M:O:D~
DTP*472*D8*20260721~`}
            </pre>
            <p className="mt-4 text-xs text-slate-400">
              Generated automatically from a scanned ADA J430D — image to 837D text file.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* CAPABILITIES */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Digital mailroom & data capture
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Dental claim form OCR for real-world claims
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* DEVELOPER API & SDK */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            For developers
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            ADA J430D parsing API & dental claims OCR API
          </h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            Integrate dental claim capture directly: an X12 837D generator from paper-claim JSON/XML,
            and an SDK to convert image to 837D EDI.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {devFeatures.map((d, i) => (
          <Reveal key={d.title} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                <d.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          ADA J430D OCR software FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Dental claim data extraction, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about Hewto.ai&apos;s ADA J430D OCR software and dental claim data
          extractor for the ADA dental claim form.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="j430d-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`j430d-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default ADAJ430D;
